import { Link } from "react-router-dom";
import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SEO from "@/components/base/SEO";
import { siteCategories } from "@/mocks/categories";
import { videos, getHotVideos, getNewVideos, featuredHeroVideos, getVideoById } from "@/mocks/videos";
import type { VideoItem } from "@/mocks/videos";

function VideoCard({ video, size = "normal" }: { video: VideoItem; size?: "large" | "normal" }) {
  return (
    <Link
      to={`/video/${video.id}`}
      className="group block cursor-pointer"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
    >
      <div className={`relative overflow-hidden rounded-lg mb-3 ${size === "large" ? "aspect-[16/9]" : "aspect-[3/4]"}`}>
        <img
          src={video.image}
          alt={video.title}
          title={`${video.title} - 桃子视频高清在线观看`}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
          <p className="text-white text-xs line-clamp-2 leading-relaxed">{video.description}</p>
        </div>
        <div className="absolute top-2 left-2 flex gap-1.5">
          {video.isHot && (
            <span className="px-2 py-0.5 text-[10px] font-semibold bg-accent-500 text-background-50 rounded-full whitespace-nowrap">
              热门
            </span>
          )}
          {video.isNew && (
            <span className="px-2 py-0.5 text-[10px] font-semibold bg-secondary-500 text-background-50 rounded-full whitespace-nowrap">
              新上
            </span>
          )}
        </div>
        <div className="absolute top-2 right-2">
          <span className="px-2 py-0.5 text-[10px] font-semibold bg-black/60 text-white rounded-full whitespace-nowrap">
            {video.rating}
          </span>
        </div>
      </div>
      <h3 className="text-sm font-semibold text-foreground-950 line-clamp-1 group-hover:text-accent-500 transition-colors">
        {video.title}
      </h3>
      <div className="flex items-center gap-2 mt-1 text-xs text-foreground-500">
        <span>{video.categoryName}</span>
        <span>·</span>
        <span>{video.year}</span>
      </div>
    </Link>
  );
}

function CategoryCard({ category }: { category: typeof siteCategories[0] }) {
  return (
    <Link
      to={`/category/${category.slug}`}
      className="flex flex-col items-center gap-2 p-4 rounded-xl bg-background-100 hover:bg-accent-100 transition-colors cursor-pointer group"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
    >
      <div className="w-12 h-12 rounded-full bg-background-50 flex items-center justify-center group-hover:bg-accent-500 transition-colors">
        <i className={`${category.icon} text-xl text-foreground-700 group-hover:text-background-50 transition-colors`}></i>
      </div>
      <span className="text-xs font-medium text-foreground-700 group-hover:text-accent-600 transition-colors whitespace-nowrap">
        {category.name}
      </span>
    </Link>
  );
}

function HomeJsonLd() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "桃子视频",
    url: "https://www.taozivideo.com",
    description: "桃子视频聚合香蕉视频、西瓜视频、蜜桃视频、樱桃视频等水果品牌影视资源，免费高清在线观看电影、电视剧、综艺，更新快不缓冲。",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://www.taozivideo.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
}

export default function Home() {
  const [activeTab, setActiveTab] = useState<"hot" | "new">("hot");
  const heroVideos = featuredHeroVideos.map((id) => getVideoById(id)!).filter(Boolean);
  const hotVideos = getHotVideos();
  const newVideos = getNewVideos();
  const displayVideos = activeTab === "hot" ? hotVideos : newVideos;

  return (
    <>
      <SEO
        title="桃子视频 - 香蕉视频西瓜视频免费高清在线观看 | 蜜桃樱桃芒果蓝莓视频电影电视剧全集不缓冲"
        description="桃子视频聚合香蕉视频、西瓜视频、蜜桃视频、樱桃视频等水果品牌影视资源，免费高清在线观看电影、电视剧、综艺，更新快不缓冲。"
        keywords="桃子视频,香蕉视频,西瓜视频,蜜桃视频,樱桃视频,芒果视频,蓝莓视频,草莓视频,高清影视,在线观看,全集"
        canonicalUrl="https://www.taozivideo.com"
        ogImage="https://readdy.ai/api/search-image?query=Chinese%20video%20streaming%20platform%20homepage%20with%20colorful%20movie%20thumbnails%2C%20warm%20modern%20design%2C%20entertainment%20branding&width=1200&height=630&seq=home-og&orientation=landscape"
      />
      <HomeJsonLd />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full pt-16">
            <div className="w-full px-4 md:px-6 py-8 md:py-12">
              <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {heroVideos.slice(0, 3).map((video, idx) => (
                    <Link
                      key={video.id}
                      to={`/video/${video.id}`}
                      className={`group relative overflow-hidden rounded-xl cursor-pointer ${
                        idx === 0 ? "lg:col-span-2 lg:row-span-2" : ""
                      }`}
                      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    >
                      <div className={idx === 0 ? "aspect-[16/9] lg:aspect-auto lg:h-full" : "aspect-[16/9]"}>
                        <img
                          src={video.image}
                          alt={video.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-0.5 text-[10px] font-semibold bg-accent-500 text-background-50 rounded-full">
                            热播
                          </span>
                          <span className="text-xs text-white/80">{video.brand}</span>
                        </div>
                        <h2 className="text-lg md:text-xl font-bold text-white mb-1">{video.title}</h2>
                        <p className="text-xs text-white/70 line-clamp-1">{video.description}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Category Navigation */}
          <section className="w-full py-8 md:py-10">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-7xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-bold text-foreground-950">影视分类</h2>
                  <Link
                    to="/category/all"
                    className="text-sm text-foreground-500 hover:text-accent-500 transition-colors flex items-center gap-1 whitespace-nowrap"
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                  >
                    查看全部 <i className="ri-arrow-right-line text-xs"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-4 sm:grid-cols-4 md:grid-cols-8 gap-3">
                  {siteCategories.map((cat) => (
                    <CategoryCard key={cat.id} category={cat} />
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Hot / New Tabs */}
          <section className="w-full py-8 md:py-10 bg-background-100/50">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-7xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-1 bg-background-200/60 rounded-full p-1">
                    <button
                      onClick={() => setActiveTab("hot")}
                      className={`px-5 py-2 text-sm font-medium rounded-full transition-all whitespace-nowrap cursor-pointer ${
                        activeTab === "hot"
                          ? "bg-background-50 text-foreground-950 shadow-sm"
                          : "text-foreground-500 hover:text-foreground-700"
                      }`}
                    >
                      热门推荐
                    </button>
                    <button
                      onClick={() => setActiveTab("new")}
                      className={`px-5 py-2 text-sm font-medium rounded-full transition-all whitespace-nowrap cursor-pointer ${
                        activeTab === "new"
                          ? "bg-background-50 text-foreground-950 shadow-sm"
                          : "text-foreground-500 hover:text-foreground-700"
                      }`}
                    >
                      最新更新
                    </button>
                  </div>
                  <Link
                    to="/category/all"
                    className="text-sm text-foreground-500 hover:text-accent-500 transition-colors flex items-center gap-1 whitespace-nowrap"
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                  >
                    更多 <i className="ri-arrow-right-line text-xs"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {displayVideos.map((video) => (
                    <VideoCard key={video.id} video={video} />
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* All Videos Grid */}
          <section className="w-full py-8 md:py-10">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-7xl mx-auto">
                <h2 className="text-lg font-bold text-foreground-950 mb-6">全部影视</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4" data-product-shop>
                  {videos.map((video) => (
                    <VideoCard key={video.id} video={video} />
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Stats Section */}
          <section className="w-full py-12 md:py-16 bg-background-100/50">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-7xl mx-auto">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                  {[
                    { value: "10万+", label: "影视资源" },
                    { value: "1.2亿+", label: "月活用户" },
                    { value: "99.9%", label: "播放成功率" },
                    { value: "8个", label: "合作品牌" },
                  ].map((stat) => (
                    <div key={stat.label} className="p-6 rounded-xl bg-background-50">
                      <div className="text-2xl md:text-3xl font-bold text-accent-500 mb-1">{stat.value}</div>
                      <div className="text-sm text-foreground-500">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}